import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DashboardTemplateValues } from 'src/app/modules/shared/entities/dashboard-template-values.class';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EmailPattern } from 'core/common-entities/email-pattern.class';
import { AddressService } from 'core/address/address.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { UtilsService } from 'core/utils/utils.service';
import { countriesWithPostalCodeField } from 'core/app-info/country-specifics.enum';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { LeadDetails } from '../../../entities/leadDetails';
import { LeadNote } from '../../../entities/leadNote';

@Component({
  selector: 'app-edit-lead-dialog',
  templateUrl: './edit-lead-dialog.component.html',
  styleUrls: ['./edit-lead-dialog.component.scss'],
  standalone: false
})
export class EditLeadDialogComponent implements OnInit, OnDestroy {
  appStrings: AppLocalization;
  isMobile = false;
  dateFormat = this.appInfoService.dateFormat;
  countryCode = this.appInfoService.userData.UserInfo.CountryCode || 'US';
  zipLabel: string;
  loading = true;
  dialogData: any;
  date: string;
  lastUpdated: string;
  notes: LeadNote[];
  accountId: string;
  editLeadForm: any;
  buttons = [
    { label: 'Close', type: 'secondary' },
    { label: 'Save', type: 'primary' },
  ];
  requiredContactFields = ['firstName', 'lastName', 'phone', 'email'];

  isRegionCountyBusiness = false;
  isSelectBusinessState = false;
  isHiddenBusinessState = false;
  businessStates: { value: string, label: string }[] = [];
  businessRegions: { value: string, label: string }[] = [];
  businessCounties: { value: string, label: string }[] = [];

  isRegionCountyContact = false;
  isSelectContactState = false;
  isHiddenContactState = false;
  contactStates: { value: string, label: string }[] = [];
  contactRegions: { value: string, label: string }[] = [];
  contactCounties: { value: string, label: string }[] = [];

  statuses: { value: number, label: string }[];
  sources: { value: number, label: string }[];
  isStatusNoteVisible = false;
  accountManagers: { value: string, label: string }[];
  countries: { value: string, label: string }[];
  templateValuesState: any[];
  templateValuesRegion: any;
  templateValuesCounty: any;
  companies: { value: number, label: any }[];

  isNewContact = false;

  subscriptions = new Subscription();
  constructor(
    public fb: FormBuilder,
    public dialogRef: MatDialogRef<EditLeadDialogComponent>,
    private appInfoService: AppInfoService,
    private dashboardService: DashboardService,
    private addressService: AddressService,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.dialogData = data;
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.zipLabel = countriesWithPostalCodeField[this.countryCode] ? this.appStrings.accounts_postalCode : this.appStrings.contacts_zip;
    this.addressService.getTemplateValues(([countryValues, stateValues, regionValues, countyValues]) => {
      this.countries = this.utilsService.mapObjectToSelect(countryValues.Countries, 'Key', 'Value');
      this.templateValuesState = this.addressService.getStateListPerCountry(stateValues);
      this.templateValuesRegion = this.addressService.getRegionListPerCountry(regionValues, countryValues.Countries);
      this.templateValuesCounty = this.addressService.getCountyListPerRegion(countyValues);
      this.dashboardService.getStaticValues((templateValues: DashboardTemplateValues) => {
        this.statuses = this.utilsService.mapNumberKeyValueOptions(templateValues.LeadActionStatus);
        this.sources = this.utilsService.mapNumberKeyValueOptions(templateValues.SourceType);
      });
      this.dashboardService.getFollowupAccountManagers((list: AccountManager[]) => {
        this.accountManagers = this.utilsService.mapObjectToSelect(list, 'id', 'name');
      });
      this.dashboardService.getLeadInfo(this.dialogData.leadId, this.dialogData.contactId).subscribe((result: LeadDetails) => {
        this.date = result.date;
        this.lastUpdated = result.lastUpdated;
        this.notes = result.notes;
        this.accountId = result.accountId;
        this.editLeadForm = this.fb.group({
          leadId: [result.leadId],
          business: this.fb.group({
            leadOwnerId: [result.business.leadOwnerId, Validators.required],
            companyId: [result.business.companyId],
            companyName: [result.business.companyName, Validators.required],
            sourceTypeId: [result.business.sourceTypeId, Validators.required],
            address1: [result.business.address1, Validators.maxLength(30)],
            address2: [result.business.address2, Validators.maxLength(30)],
            countryId: [result.business.countryId],
            stateId: [result.business.stateId],
            province: this.fb.group({
              id: [result.business.province.id],
              name: [result.business.province.name],
            }),
            regionId: [result.business.regionId],
            countyId: [result.business.countyId],
            city: [result.business.city, [Validators.maxLength(30), Validators.required]],
            zipCode: [result.business.zipCode],
          }),
          contact: this.fb.group({
            contactId: [result.contact?.contactId],
            firstName: [result.contact?.firstName ?? ''],
            lastName: [result.contact?.lastName ?? ''],
            title: [result.contact?.title ?? '', Validators.maxLength(30)],
            department: [result.contact?.department ?? '', Validators.maxLength(30)],
            email: [result.contact?.email ?? '', [
              Validators.email,
              Validators.pattern(EmailPattern.pattern),
              Validators.maxLength(100)
            ]],
            phone: [result.contact?.phone ?? ''],
            addressId: [result.contact?.addressId],
            address1: [result.contact?.address1 ?? '', Validators.maxLength(30)],
            address2: [result.contact?.address2 ?? '', Validators.maxLength(30)],
            countryId: [result.contact?.countryId ?? this.countries[0].value],
            stateId: [result.contact?.stateId],
            province: this.fb.group({
              id: [result.contact?.province?.id],
              name: [result.contact?.province?.name],
            }),
            regionId: [result.contact?.regionId],
            countyId: [result.contact?.countyId],
            city: [result.contact?.city ?? '', Validators.maxLength(30)],
            zipCode: [result.contact?.zipCode ?? ''],
          }),
          status: [result.status],
          statusNote: ['']
        });

        this.setBusinessStates(result.business.countryId);
        this.setContactStates(result.contact?.countryId);
        this.setBusinessCounties(result.business.regionId);
        this.setContactCounties(result.contact?.regionId);

        this.subscriptions.add(this.editLeadForm.controls.business.controls.leadOwnerId.valueChanges.subscribe(() => {
          const businessControls = this.editLeadForm.controls.business.controls;
          if (businessControls.leadOwnerId.dirty) {
            businessControls.companyId.setValue(this.appInfoService.emptyGuid);
          }
        }));
        this.subscriptions.add(this.editLeadForm.controls.business.controls.companyName.valueChanges.subscribe(() => {
          const businessControls = this.editLeadForm.controls.business.controls;
          if (businessControls.companyName.dirty) {
            businessControls.companyId.setValue(this.appInfoService.emptyGuid);
          }
        }));
        this.loading = false;
      });
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  btnCallback(i: number) {
    if (i === 0) {
      this.dialogRef.close();
    } else {
      this.editLeadForm.markAllAsTouched();
      this.checkContactValidation();
      const { value, valid } = this.editLeadForm;
      if (valid) {
        this.loading = true;
        this.addressService.normalizeAddressState(value.business);
        if (this.isContactEmpty()) {
          this.addressService.normalizeAddressState(value.contact);
        } else {
          value.contact = null;
        }
        this.dashboardService.saveLeadInfo(value).subscribe(() => {
          this.loading = false;
          this.dialogRef.close({ shouldRefresh: true });
        });
      }
    }
  }

  handleCountryChange(isContact = false) {
    if (isContact) {
      const contactControls = this.editLeadForm.controls.contact.controls;
      this.setContactStates(contactControls.countryId.value);
      this.resetInputsOnCountryChange(contactControls);
    } else {
      const businessControls = this.editLeadForm.controls.business.controls;
      this.setBusinessStates(businessControls.countryId.value);
      this.resetInputsOnCountryChange(businessControls);
    }
  }

  handleStatusChange() {
    const controls = this.editLeadForm.controls;
    if (this.dashboardService.leadsStatusNeedingNote.includes(Number(controls.status.value))) {
      this.isStatusNoteVisible = true;
      controls.statusNote.setValidators([Validators.required]);
    } else {
      this.isStatusNoteVisible = false;
      controls.statusNote.removeValidators([Validators.required]);
    }
    controls.statusNote.updateValueAndValidity();
  }

  handleRegionChange(isContact = false) {
    if (isContact) {
      const contactControls = this.editLeadForm.controls.contact.controls;
      this.contactCounties = this.getCountiesByRegion(contactControls.regionId.value);
      contactControls.countyId.reset();
      contactControls.countyId.enable();
      contactControls.countyId.updateValueAndValidity();
    } else {
      const businessControls = this.editLeadForm.controls.business.controls;
      this.businessCounties = this.getCountiesByRegion(businessControls.regionId.value);
      businessControls.countyId.enable();
      businessControls.countyId.reset();
      businessControls.countyId.updateValueAndValidity();
    }
  }

  setBusinessCounties(regionId: number) {
    if (regionId) {
      this.businessCounties = this.getCountiesByRegion(this.editLeadForm.controls.business.controls.regionId.value);
    }
  }

  setContactCounties(regionId: number) {
    if (regionId) {
      this.contactCounties = this.getCountiesByRegion(this.editLeadForm.controls.contact.controls.regionId.value);
    }
  }

  private setBusinessStates(countryId: number) {
    if (countryId) {
      this.isSelectBusinessState = this.addressService.countryUsesStateDropdown(countryId);
      if (this.isSelectBusinessState) {
        this.businessStates = this.getStatesByCountry(countryId);
      }
      this.isRegionCountyBusiness = this.addressService.countryUsesRegionStateDropdown(countryId);
      if (this.isRegionCountyBusiness) {
        this.businessRegions = this.getRegionsByCountry(countryId);
      }
      this.isHiddenBusinessState =
        !this.isSelectBusinessState &&
        !this.isRegionCountyBusiness &&
        !this.addressService.countryUsesProvinceFreeInput(countryId);
    } else {
      this.isSelectBusinessState = true;
      this.isRegionCountyBusiness = false;
      this.isHiddenBusinessState = false;
    }

    this.setStateValidators();
  }

  private setContactStates(countryId: number) {
    if (countryId) {
      this.isSelectContactState = this.addressService.countryUsesStateDropdown(countryId);
      if (this.isSelectContactState) {
        this.contactStates = this.getStatesByCountry(countryId);
      }
      this.isRegionCountyContact = this.addressService.countryUsesRegionStateDropdown(countryId);
      if (this.isRegionCountyContact) {
        this.contactRegions = this.getRegionsByCountry(countryId);
      }
      this.isHiddenContactState =
        !this.isSelectContactState &&
        !this.isRegionCountyContact &&
        !this.addressService.countryUsesProvinceFreeInput(countryId);
    } else {
      this.isSelectContactState = true;
      this.isRegionCountyContact = false;
      this.isHiddenContactState = false;
    }
  }

  private getStatesByCountry(countryId: number) {
    return this.utilsService.mapObjectToSelect(
      this.templateValuesState[countryId] ?? [],
      'stateId',
      'stateName'
    );
  }

  private getRegionsByCountry(countryId: number) {
    return this.utilsService.mapObjectToSelect(
      this.templateValuesRegion[countryId] ?? [],
      'regionId',
      'regionName'
    );
  }

  private getCountiesByRegion(regionId: number) {
    return this.utilsService.mapObjectToSelect(
      this.templateValuesCounty[regionId] ?? [],
      'id',
      'countyName'
    );
  }

  private resetInputsOnCountryChange(control: any) {
    control.stateId.reset();
    control.province.controls.name.reset();
    control.regionId.reset();
    control.countyId.reset();
    control.countyId.disable();
    control.stateId.updateValueAndValidity();
    control.regionId.updateValueAndValidity();
    control.countyId.updateValueAndValidity();
    control.province.controls.name.updateValueAndValidity();
  }

  private setStateValidators() {
    const businessControls = this.editLeadForm.controls.business.controls;
    businessControls.stateId.setValidators(
      !this.isHiddenBusinessState && this.isSelectBusinessState ? [Validators.required] : ''
    );
    businessControls.province.controls.name.setValidators(
      !(this.isHiddenBusinessState || this.isSelectBusinessState || this.isRegionCountyBusiness) ? [Validators.required] : ''
    );
  }

  private checkContactValidation() {
    if (this.isContactEmpty()) {
      this.requiredContactFields.forEach(field => {
        this.editLeadForm.controls.contact.controls[field].addValidators([Validators.required]);
      });
    } else {
      this.requiredContactFields.forEach(field => {
        this.editLeadForm.controls.contact.controls[field].removeValidators([Validators.required]);
      });
    }
    this.requiredContactFields.forEach(field => {
      this.editLeadForm.controls.contact.controls[field].updateValueAndValidity();
    });
  }

  private isContactEmpty() {
    const control = { ...this.editLeadForm.controls.contact.value };
    delete control.countryId;
    // check if any value except countryId
    return this.utilsService.hasAnyValue(control);
  }
}
